import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCampaignPageParam } from '../useCampaignPageParam';
import {
  useCampaignPageSizeParam,
  PAGE_SIZE_OPTIONS,
} from '../useCampaignPageSizeParam';
import { useCampaignPillParam } from '../useCampaignPillParam';
import type { CampaignSearch } from '../search';
import { Article, PaperPlaneTilt, Play, Plus, VideoCamera, FileText, UserCheck, ChartLineUp } from '@phosphor-icons/react';
import { EmptyState } from '../../components/common/EmptyState';
import { mapThreadMessages } from '../../components/common/mapThreadMessages';
import { Info } from '../../design-system/icons';
import { SimpleTooltip } from '../../components/ui/tooltip';
import {
  createCampaignDetailApi,
  useContentDetail,
  useFinalizedKPI,
  useRosterCounts,
  useRosterThread,
  useScriptDetail,
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useCampaignHeaderQuery,
  useAssignProducts,
  campaignKeys,
  campaignCopy,
  type RosterRowRef,
} from '@discovr/features';
import type {
  Post,
  RosterEntryFinalized,
  ScriptDetail,
  ScriptDoc,
} from '@discovr/types';
import {
  fmtDeliverableType,
} from '@discovr/types';
import {
  mainPost,
  canViewScript,
  canViewContent,
  visibleFinalizedPhases,
  filterFinalizedPills,
  deliverableContentKind,
} from './ConfirmedTab.helpers';
import { resolvePipeline } from '../utils/campaignPipeline';
import { HELD_ACTION_REASON, isPostHeld } from './ConfirmedTab.hold';
import { useConfirmedHold } from './ConfirmedTab.holdControls';
import { useConfirmedRollback } from './ConfirmedTab.rollbackControls';
import { NoCodeChip, useNoCodeBriefGuard } from './ConfirmedTab.noCode';
import { DisabledReason } from './ConfirmedTab.disabledReason';
import { HoldStatusLine } from './HoldStatusLine';
import { ROW_MENU_OPEN_ROW_CLASS, useConfirmedRowMenuColumn, withTrailingColumn } from './ConfirmedTab.rowMenuColumn';
import { EstimatedCPVCell } from './CreatorsTab/EstimatedCPVCell';
import { ProductAssignCell } from './ProductAssignCell';
import { PreviousProductsPrompt } from './PreviousProductsPrompt';
import { isProductsLocked, shouldOfferPreviousProducts, useCanResolvePreviousProducts } from './previousProducts';
import { Button } from '../../components/ui/button';
import { fmtDateTime, contentKindLabel } from '../utils/campaignDetailHelpers';
import { formatMoneyExact, moneyFitClass } from '../../lib/format';
import { PlatformCreatorCell, resolveCreatorPlatformProfile } from '../../lib/platformProfile';
import { VideoPlayerPopup } from '../../campaign-reporting/VideoPlayerPopup';
import { RosterTableShell } from './parts/RosterTableShell';
import { RosterKpiCard } from './parts/RosterKpiCard';
import { RosterScreenHeader } from './parts/RosterScreenHeader';
import { RosterSearchSort } from './parts/RosterSearchSort';
import { useCampaignPanelControls } from '../useCampaignPanelControls';
import { useCampaignFinalizedReviewControls } from '../useCampaignFinalizedReviewControls';
import { useCampaignVideoControls } from '../useCampaignVideoControls';
import { useCampaignVideoDeepLink } from '../useCampaignVideoDeepLink';
import { StatusBadge } from './parts/StatusBadge';
import { CreatorAnalysisButton } from './parts/CreatorAnalysisButton';
import { rowDisplayName } from '../utils/rowDisplayName';
import { ScriptReviewPanel, type ScriptReviewAiEnvelope } from '../../components/common/ScriptReview/ScriptReviewPanel';
import { ContentReviewPanel } from '../../components/common/ContentReview/ContentReviewPanel';
import { ContentRowDetail, uploadCopy } from './ContentTab';
import { liveUrlCopy } from './LiveContentTab';
import { AddScriptModal } from './AddScriptModal';
import { UrlInputModal } from './UrlInputModal';
import type { ReviewThreadEntry } from '../../lib/models';
import { liveVideoThumbnailUrl } from '../../lib/youtubeThumbnail';
import { DataTable, ColumnsMenu, columnVisibilityView } from '../../components/common/DataTable';
import type { ColumnDef } from '../../components/common/DataTable';
import {
  useRosterStatusTabs,
  useRosterTabSort,
  rosterSortStorageKey,
  useRosterColumnPreferences,
} from '../hooks';
import type { RosterPage, RosterPillDef } from '../hooks';
import { useRosterSearch } from '../hooks/useRosterSearch';
import { usePublishSharePageInfo } from '../hooks/usePublishSharePageInfo';
import { usePublicView } from '../publicView';
import { usePublicPillCapture, usePublicInsightsCapture } from '../usePublicPillCapture';
import { adaptPublicRosterRowsAsFinalizedRows } from './ConfirmedTab.publicRosterRowAdapter';

/** No-op used for every pagination/pill callback in public read-only mode —
 *  belt-and-braces alongside `PublicCampaignShell`'s click-interceptor
 *  (which cancels the click before it ever reaches these handlers in the
 *  real wiring). Kept as a single stable reference so it never appears as a
 *  changed prop across renders. Mirrors CreatorsTab.tsx's PUBLIC_NOOP. */
const PUBLIC_NOOP = () => {};

// Singleton factory for the per-pill queries below.
const unifiedApi = createCampaignDetailApi();

/** "Fetch everything" sentinel — matches the value every other roster tab
 *  uses for its "fetch the full bucket, paginate client-side" pill query. */
const MAX_FETCH_LIMIT = 10000;

// Production-phase gating moved to posts[0].status (see
// ConfirmedTab.helpers). The roster row's own `status` is frozen at
// accepted_by_creator under the posts/addons cutover and no longer
// carries the script → content → live lifecycle, so the per-artifact
// gates (mainPost / canViewScript / canViewContent) read the main post
// instead. Rationale preserved: "only show … if uploaded" → an artifact
// for phase X is viewable once posts[0] has reached that phase's review.

// Finalized sub-tabs slice the post-approval roster by the main post's
// phase. All — every finalized row. Script — script_pending +
// script_review. Content — content_pending + content_review. Going live —
// live_pending + live.
type FinalizedSubTab = 'all' | 'script' | 'content' | 'live' | 'hold' | 'unassigned';

// Bucketed phase membership — a finalized creator belongs to exactly ONE phase,
// the one its main post is currently in. Mirrors the BE's `?phase=` buckets
// (internal/campaigns/unified_roster.go: finalizedPhaseStatuses) so a pill's
// list and its badge can never disagree, and Script+Content+Live = All.
//
// This REPLACED a cumulative `postRank(c) >= minRank` filter, which was a real
// bug: Script (rank >= script_review) excluded script_pending creators entirely
// and leaked every content/live creator in.
const FINALIZED_PHASE_STATUSES: Record<
  Exclude<FinalizedSubTab, 'all' | 'unassigned' | 'hold'>,
  ReadonlyArray<NonNullable<ReturnType<typeof mainPost>>['status']>
> = {
  script: ['script_pending', 'script_review'],
  content: ['content_pending', 'content_review'],
  live: ['live_pending', 'live'],
};

const FINALIZED_PILLS: RosterPillDef<FinalizedSubTab>[] = [
  { key: 'all', label: 'All' },
  { key: 'script', label: 'Script' },
  { key: 'content', label: 'Content' },
  { key: 'live', label: 'Going live' },
];

const HOLD_PILL: RosterPillDef<FinalizedSubTab> = { key: 'hold', label: 'Hold' };
const holdCopy = campaignCopy.creator.hold;

/** Wording matters here: the number is main-post cost ÷ estimated views, both
 *  frozen when the creator accepted. It is NOT locked budget ÷ avg views —
 *  add-ons sit in the budget but never in this figure. */
const EST_CPV_TOOLTIP =
  'Committed cost per estimated view · locked when the creator accepted';

const EST_VIEWS_LABEL = 'Est. Views';

/** Compact action button used for the Script/Content cells on each
 *  Finalized row — enforced fixed width + identical typography so the two
 *  line up. `enabled` covers both the "view" state (artifact ready) and
 *  the "add" state (not yet added, admin can submit it) — callers pick the
 *  icon/onClick for whichever state applies; disabled only when neither. */
function FinalizedRowAction({
  icon,
  label,
  enabled,
  onClick,
  disabledReason,
}: {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  onClick?: () => void;
  disabledReason?: string;
}) {
  const button = (
    <Button
      variant="ghost"
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      className={
        'inline-flex w-24 items-center justify-center gap-1 rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors ' +
        (enabled
          ? 'border-border bg-card text-foreground hover:bg-muted'
          : 'border-border bg-muted text-muted-foreground cursor-not-allowed')
      }
    >
      {icon}
      <span>{label}</span>
    </Button>
  );
  return <DisabledReason reason={enabled ? null : (disabledReason ?? null)}>{button}</DisabledReason>;
}

// resolveCreatorReach + DELIVERABLE_MULTIPLIERS removed: EST. REACH now
// reads estimated_reach directly from the BE (same fix as CreatorsTab
// Phase 2.3). The local formula was reading deliverables.is_selected
// which is absent on Finalized rows (they use the BE-filtered selected
// list instead).


const PRODUCTS_LOCKED_LABEL = campaignCopy.creator.previousProducts.locked;

export function ConfirmedTab() {
  const { campaignId } = useCampaignUiStore();
  const campaignPipeline = useCampaignUiStore((s) => s.campaignPipeline);
  const pipeline = resolvePipeline(campaignPipeline);
  // Public (no-login) share view — see packages/ui/src/campaign-detail/
  // publicView.tsx. Mirrors CreatorsTab.tsx's own doc comment: campaignId
  // never gets populated in public mode (PublicShareViewPage resets the
  // store on mount), so every campaignId-gated query below stays disabled on
  // its own; `enabled: !isPublicView` on the aggregate/list queries is
  // explicit, documented insurance on top of that, not the only thing
  // standing between this tab and a real network call.
  const { isPublicView, grant, rosterPage } = usePublicView();
  const {
    permissions,
    handleConfirmRosterBrief,
    handleAdminReviewScript,
    confirmScriptUpload,
    handleAdminSubmitContent,
    handleSetLiveUrl,
    lane,
    holdPost,
    releasePost,
    rollbackApproval,
    rollbackAcceptance,
  } = useCampaignCapabilitiesStore();
  const { openInsights } = useCampaignPanelControls();
  const { openHold, openRelease, dialogs: holdDialogs } = useConfirmedHold();
  const { openRollback, dialogs: rollbackDialogs } = useConfirmedRollback();
  const { hasNoCode, guardSend, dialog: noCodeDialog } = useNoCodeBriefGuard(
    campaignId ?? '',
    !isPublicView,
    lane === 'admin',
  );
  // Lane, not canHoldCreator: an admin_member without the hold grant still needs
  // the badge that explains disabled actions; brand/public never see hold data.
  const showHold = lane === 'admin' && !isPublicView;
  const canManageHold = showHold && !!permissions?.canHoldCreator;
  const canRollback = showHold && !!permissions?.canRollbackApproval;
  const showRowMenu = canManageHold || canRollback;
  const isRowHeld = useCallback((post?: Post) => showHold && isPostHeld(post), [showHold]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Approve-from-Confirmed: mirrors ScriptsTab's handleApproveScript. Busy flag
  // is local state (the capabilities callback is fire-and-forget async and
  // doesn't expose isPending); justApprovedIds keeps the panel showing a green
  // "Approved" button until the user closes it, even though the snapshot
  // `readOnlyDialog.creator` captured at open time doesn't itself refresh.
  const [scriptActionBusy, setScriptActionBusy] = useState(false);
  const [justApprovedIds, setJustApprovedIds] = useState<Set<string>>(new Set());
  const handleApproveScript = async (entryId: string) => {
    setScriptActionBusy(true);
    try {
      await handleAdminReviewScript?.(entryId, 'approve');
      setJustApprovedIds((prev) => new Set(prev).add(entryId));
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.rosterTab(campaignId ?? '', 'finalized'),
      });
    } finally {
      setScriptActionBusy(false);
    }
  };
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId ?? '', { enabled: !isPublicView });
  const campaignPlatform = campaignHeader?.platform;

  // Multi-Product Campaigns: the products catalog + the >= 2 gate. Computed here
  // (above the roster-tabs hook) so the Unassigned pill can be added to the pill
  // list conditionally and the Products column can gate on it.
  const productCatalog = useMemo(
    () => (campaignHeader?.products ?? []).map((p) => ({ product_id: p.product_id, name: p.name })),
    [campaignHeader?.products],
  );
  const showProducts = productCatalog.length >= 2;

  // ── Paginated finalized list via the shared roster-status-tabs hook ──────────
  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam();
  const [rawPhaseTab, setPhaseTab] = useCampaignPillParam('all') as [FinalizedSubTab, (v: string) => void];
  // Guard invalid/unavailable deep links: `unassigned` is only a real pill on
  // multi-product campaigns (showProducts). Landing on it elsewhere (or any
  // unknown pill) falls back to 'all' so the tab can never hang on a pill that
  // isn't rendered.
  //
  // Public mode pins the active sub-tab to whichever pill the grant shared —
  // there is only ONE pill's real row data available (mirrors CreatorsTab's
  // creatorsSubTab pinning), so the UI must not let the visitor land on (or
  // the URL default to) any other one.
  const phaseTab: FinalizedSubTab =
    isPublicView && grant?.pill
      ? (grant.pill as FinalizedSubTab)
      : (rawPhaseTab === 'unassigned' && !showProducts) ||
          (rawPhaseTab === 'hold' && !canManageHold) ||
          ((rawPhaseTab === 'script' || rawPhaseTab === 'content') &&
            !visibleFinalizedPhases(campaignPipeline).includes(rawPhaseTab))
        ? 'all'
        : rawPhaseTab;

  // Clicking a status pill this link didn't grant used to be a silent no-op
  // (`PUBLIC_NOOP`) — now it prompts sign-in with the click captured as a
  // resumable `select_pill` pending action. Clicking the pill this link
  // already granted stays a no-op. See usePublicPillCapture's own doc
  // comment — this used to be ~20 lines hand-duplicated across five tabs.
  const handlePublicPillClick = usePublicPillCapture('confirmed', phaseTab);

  // "View analysis" resume — see usePublicPillCapture's doc comment.
  const handlePublicViewInsightsCapture = usePublicInsightsCapture('confirmed');
  const handlePublicViewInsights = useCallback(
    (row: RosterEntryFinalized) => handlePublicViewInsightsCapture(row.platform?.username?.trim()),
    [handlePublicViewInsightsCapture],
  );

  // Active bucket total from /roster/counts (deduped by React Query with the
  // counts call below) — drives the hybrid search decision. Disabled in
  // public mode: a public link never carries campaign-wide counts.
  const { data: countsForSearch } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const activeFinalizedTotal = !countsForSearch
    ? undefined
    : phaseTab === 'all'
      ? countsForSearch.finalized_total
      : countsForSearch.phase[phaseTab];
  // Stable identity so the search-reset effect dep does not churn every render.
  const resetPageForSearch = useCallback(() => setPage(1), [setPage]);
  const searchState = useRosterSearch(activeFinalizedTotal, pageSize, {
    onQueryChange: resetPageForSearch,
  });

  const getRowSearchText = useCallback((c: RosterEntryFinalized) => {
    return [
      (c.platform as { name?: string } | undefined)?.name ?? '',
      c.creator?.name ?? '',
      c.platform?.username ?? '',
    ].join(' ');
  }, []);

  // SERVER-SIDE pagination: only the ACTIVE pill fetches, and it fetches exactly
  // one page. Phase filtering moved to the BE (`?phase=`), which also FIXES the
  // old client filter: it used cumulative `postRank(c) >= minRank`, so Script
  // excluded script_pending and leaked in content/live rows. The BE buckets
  // strictly (script = script_pending + script_review), so Script+Content+Live
  // now partition the finalized set and reconcile with the badge counts.
  //
  // Two cases still need the whole bucket, and each builds its own page from it:
  //   - `unassigned` — an entry-level predicate (no products assigned), not a
  //     production phase, so `?phase=` can't express it yet.
  //   - active search — server-side search lands with Atlas Search; until then
  //     searching the current 25 rows would silently miss matches, so we keep
  //     today's whole-bucket fuzzy behaviour while a query is typed.
  // Per-tab column sort (persisted), threaded into the query below.
  const rosterSort = useRosterTabSort<FinalizedSubTab>(rosterSortStorageKey('confirmed', campaignId), phaseTab);

  const buildPageQuery = useCallback(
    (pill: RosterPillDef<FinalizedSubTab>, pageNum: number, pageSize: number) => {
      const cid = campaignId ?? '';
      // The backend ignores sort on the Hold bucket.
      const sortSpec = pill.key === 'hold' ? undefined : rosterSort.specFor(pill.key);
      // Unassigned always needs the whole bucket (it's an entry-level predicate,
      // not a ?phase=). Search needs it only in SERVER mode (large bucket) — small
      // buckets take the normal page path and filter on display via clientFilter.
      const needsWholeBucket = pill.key === 'unassigned' || !!searchState.serverSearch;

      if (needsWholeBucket) {
        return {
          // Key by (pill, search) — NOT page/size. The queryFn caches the whole
          // filtered bucket once per search term; `select` derives the visible
          // page, so page turns re-slice the cached bucket instead of refetching
          // 10k rows, and a changed search term is a distinct cache slot (the
          // stale-results bug review caught). The page/size-keyed variant that
          // fixed the earlier render-loop is superseded: with select-derived
          // slicing the query config no longer changes with page/size at all.
          queryKey: campaignKeys.rosterTab(cid, 'finalized', {
            phase: `${pill.key}:bucket`,
            search: searchState.serverSearch,
            limit: MAX_FETCH_LIMIT,
          }),
          queryFn: async () => {
            const res = await unifiedApi.listFinalized(cid, { page: 1, limit: MAX_FETCH_LIMIT });
            let rows = res.entries;
            if (pill.key === 'unassigned') {
              rows = rows.filter((c) => (c.assigned_products ?? []).length === 0);
            } else if (pill.key === 'hold') {
              rows = rows.filter((c) => isPostHeld(mainPost(c)));
            } else if (pill.key !== 'all') {
              const allowed = FINALIZED_PHASE_STATUSES[pill.key];
              rows = rows.filter((c) => {
                const post = mainPost(c);
                return post?.status !== undefined && allowed.includes(post.status) && !isPostHeld(post);
              });
            }
            if (searchState.serverSearch) {
              const q = searchState.serverSearch.toLowerCase();
              rows = rows.filter((c) => getRowSearchText(c).toLowerCase().includes(q));
            }
            return { entries: rows, total: rows.length, totalPages: 1 };
          },
          select: (bucket: RosterPage<RosterEntryFinalized>): RosterPage<RosterEntryFinalized> => {
            const start = (pageNum - 1) * pageSize;
            return {
              entries: bucket.entries.slice(start, start + pageSize),
              total: bucket.total,
              totalPages: Math.max(1, Math.ceil(bucket.total / pageSize)),
            };
          },
          staleTime: 30_000,
          // `enabled: !isPublicView` below is a REAL, independently-honored
          // gate, not redundant defense-in-depth — see useRosterStatusTabs's
          // queryConfigs (`built.enabled` is ANDed with `!!campaignId`, never
          // clobbered by it) and CreatorsTab.tsx's identical comment.
          enabled: !isPublicView,
        };
      }

      return {
        queryKey: campaignKeys.rosterTab(cid, 'finalized', {
          phase: pill.key,
          page: pageNum,
          limit: pageSize,
          sort_by: sortSpec?.by,
          sort_dir: sortSpec?.dir,
        }),
        enabled: !isPublicView,
        queryFn: () =>
          unifiedApi
            .listFinalized(cid, {
              page: pageNum,
              limit: pageSize,
              // 'unassigned' never reaches here — it took the whole-bucket
              // branch above, since it isn't a production phase.
              phase:
                pill.key === 'all' || pill.key === 'unassigned' ? undefined : pill.key,
              sort_by: sortSpec?.by,
              sort_dir: sortSpec?.dir,
            })
            .then((res) => ({
              entries: res.entries,
              total: res.total,
              totalPages: res.totalPages,
            })),
        staleTime: 30_000,
      };
    },
    [campaignId, searchState.serverSearch, getRowSearchText, isPublicView, rosterSort],
  );

  // Multi-Product Campaigns: append the Unassigned pill only at >= 2 products.
  const finalizedPillDefs = useMemo<RosterPillDef<FinalizedSubTab>[]>(
    () => [
      ...filterFinalizedPills(FINALIZED_PILLS, campaignPipeline),
      ...(canManageHold ? [HOLD_PILL] : []),
      ...(showProducts ? [{ key: 'unassigned' as const, label: 'Unassigned' }] : []),
    ],
    [showProducts, canManageHold, campaignPipeline],
  );

  const effectivePhaseTab: FinalizedSubTab = phaseTab;
  const canSort = !isPublicView && phaseTab !== 'hold';

  // Badge counts for every pill from ONE aggregation — no pill fetches rows just
  // to show its number. While a search is active the badges intentionally keep
  // showing full-tab totals (search narrows the list, not the tab's size).
  // Disabled in public mode: a public link never carries campaign-wide counts.
  const { data: rosterCounts } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const finalizedCounts = useMemo<Partial<Record<FinalizedSubTab, number>>>(
    () => ({
      all: rosterCounts?.finalized_total ?? 0,
      script: rosterCounts?.phase.script ?? 0,
      content: rosterCounts?.phase.content ?? 0,
      live: rosterCounts?.phase.live ?? 0,
      hold: rosterCounts?.phase.hold ?? 0,
      unassigned: rosterCounts?.phase.unassigned ?? 0,
    }),
    [rosterCounts],
  );

  const {
    pills: finalizedPills,
    pageRows: filteredCreators,
    total,
    pageCount,
    isLoading: isLoadingEntries,
  } = useRosterStatusTabs<RosterEntryFinalized, FinalizedSubTab>({
    campaignId: campaignId || undefined,
    activeTab: effectivePhaseTab,
    pills: finalizedPillDefs,
    buildPageQuery,
    counts: finalizedCounts,
    pageSize,
    page,
    setPage,
    clientFilter: searchState.clientQuery
      ? (r) => getRowSearchText(r).toLowerCase().includes(searchState.clientQuery)
      : undefined,
  });

  // Publishes pageSize/pageCount for the admin-header page-kind share button
  // (which has no table state of its own) — see usePublishSharePageInfo.
  // Disabled in public mode: that share button doesn't exist in the public
  // shell, and pageCount here is the disabled roster query's own (always
  // wrong: stuck at 1) value, not the real grant snapshot.
  usePublishSharePageInfo(pageSize, pageCount, !isPublicView);

  const allCount = finalizedCounts.all ?? 0;

  // Public mode: the granted pill's real rows, adapted from the flat
  // PublicRosterRow shape into what these columns read. `filteredCreators`
  // above stays permanently empty (its query is disabled) — this is the
  // ACTUAL data source rendered in public mode, sourced from the
  // already-fetched public payload rather than any query.
  const publicRows = useMemo(
    () => adaptPublicRosterRowsAsFinalizedRows(rosterPage?.rows ?? []),
    [rosterPage],
  );
  const displayedRows = isPublicView ? publicRows : filteredCreators;

  // Pill badges: every pill EXCEPT the granted one shows a skeleton (never a
  // false "0" — a public link never carries every pill's count, only the
  // one it granted). The granted pill shows its real row count ONLY when the
  // grant is a single page — see CreatorsTab.tsx's identical `displayPills`
  // doc comment for the full rationale (same `total_pages > 1` condition
  // also drives the pagination footer's hideSummary below — the two must
  // never disagree about whether the total is known).
  const displayPills = useMemo(() => {
    if (!isPublicView) return finalizedPills;
    const grantedPillKey = grant?.pill;
    const grantedTotalPages = rosterPage?.total_pages ?? grant?.totalPages ?? 1;
    const grantedRowCount = rosterPage?.rows.length ?? 0;
    return finalizedPills.map((p) => ({
      ...p,
      count:
        p.key === grantedPillKey
          ? grantedTotalPages > 1
            ? ('loading' as const)
            : grantedRowCount
          : ('loading' as const),
    }));
  }, [finalizedPills, isPublicView, grant?.pill, grant?.totalPages, rosterPage]);

  // Tab-aggregated KPI — BE-computed actuals across the full Finalized
  // tab. Separate query so list pagination doesn't refetch it (and an
  // SSE-driven KPI bump doesn't churn the list). Disabled in public mode: a
  // public link never carries campaign-wide actuals.
  const kpiQuery = useFinalizedKPI(campaignId ?? '', { enabled: !isPublicView });

  const [confirmingBriefId, setConfirmingBriefId] = useState<string | null>(null);

  const handleConfirmBrief = useCallback(
    async (c: RosterEntryFinalized) => {
      const legacy = c as { roster_entry_id?: string; _id?: string; id?: string };
      const rosterEntryId = String(legacy.roster_entry_id || legacy._id || c.id || '');
      if (!rosterEntryId) {
        console.error('Missing roster entry ID');
        return;
      }

      if (!handleConfirmRosterBrief) {
        console.error('handleConfirmRosterBrief not available');
        return;
      }

      setConfirmingBriefId(rosterEntryId);
      try {
        await handleConfirmRosterBrief(rosterEntryId);
      } catch (error) {
        console.error('Error confirming brief:', error);
        alert(`Error: ${error instanceof Error ? error.message : 'Failed to confirm brief'}`);
      } finally {
        setConfirmingBriefId(null);
      }
    },
    [handleConfirmRosterBrief],
  );


  // V2 (2026-05-04): Finalized rows can open a read-only Script /
  // Content side-panel showing the locked artifact + chat history. The
  // panel reuses AiDraftReviewDialog (the same component the live
  // Script/Content review tabs use) with `canReview=false` +
  // `canAddComment=false` to suppress every interactive control —
  // no Approve, no compose, no "request changes". Brand and admin
  // both see the same read-only surface; post-approval the artifact is
  // locked and chat is treated as a frozen audit trail (mirrors the
  // `selection_thread_locked` UI pattern on rejected rows).
  const [readOnlyDialog, setReadOnlyDialog] = useState<
    { creator: RosterEntryFinalized; mode: 'script' | 'content' } | null
  >(null);
  // URL write-side companion: opening the read-only panel writes
  // ?panel=script-finalized|content-finalized&creator=<username> so refresh
  // restores the same panel and a click produces a shareable link.
  const { openFinalized, closeFinalized } = useCampaignFinalizedReviewControls(
    setReadOnlyDialog,
  );
  const [videoPopup, setVideoPopup] = useState<RosterEntryFinalized | null>(null);
  // URL write-side companion for the live-video popup: ?panel=video&video=<roster_entry_id>
  const { openVideo, closeVideo } = useCampaignVideoControls<RosterEntryFinalized>(setVideoPopup);

  // Add-artifact row state for the three not-yet-added Action states below —
  // same shared AddScriptModal / UrlInputModal the live Scripts/Content/Live
  // tabs use, so Confirmed offers the identical popup + API, just reachable
  // one tab early. `null` when closed.
  const [addScriptRow, setAddScriptRow] = useState<{ id: string; name: string } | null>(null);
  const [addScriptLoading, setAddScriptLoading] = useState(false);
  const [addContentRow, setAddContentRow] = useState<
    { id: string; existingUrl: string; expectedContentKind?: string } | null
  >(null);
  const [addLiveUrlRow, setAddLiveUrlRow] = useState<{ id: string; expectedContentKind?: string } | null>(null);

  // Mirrors ScriptsTab.tsx's handleAddScript exactly (same confirmScriptUpload
  // call, same route) — Confirmed just reaches the identical modal one phase
  // early. Invalidates the finalized roster so the row flips to the read-only
  // Script view once the submit lands.
  const handleAddScript = async (_creatorId: string, rosterEntryId: string, doc: ScriptDoc) => {
    try {
      setAddScriptLoading(true);
      await confirmScriptUpload?.(rosterEntryId, doc);
      setAddScriptRow(null);
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.rosterTab(campaignId ?? '', 'finalized'),
      });
    } finally {
      setAddScriptLoading(false);
    }
  };

  // Full finalized roster (every phase, unfiltered) — used by both deep-link
  // resolvers below so a shared link still opens a row regardless of which
  // phase tab happens to be active. Same queryKey the "all" pill inside
  // useRosterStatusTabs already populates, so this is a second observer on
  // that one cache entry, not an extra network request.
  const allFinalizedQuery = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId ?? '', 'finalized', { phase: 'all', limit: MAX_FETCH_LIMIT }),
    queryFn: () =>
      unifiedApi.listFinalized(campaignId ?? '', { page: 1, limit: MAX_FETCH_LIMIT }).then((res) => res.entries),
    // `&& !isPublicView` is real, independently-honored insurance, not just
    // relying on campaignId happening to be empty — same rationale as every
    // other `enabled` gate in this file.
    enabled: !!campaignId && !isPublicView,
    staleTime: 30_000,
  });

  // URL → state reader. Resolves against the full finalized roster above —
  // every phase, not just the active pill — so a shared video link still
  // opens a row while a different phase tab is selected. Open-once guard so
  // closing doesn't re-open on re-render.
  const { videoId: deepLinkVideoId, row: deepLinkVideoRow } = useCampaignVideoDeepLink(
    allFinalizedQuery.data ?? [],
    (r) => r.id,
  );
  const videoDeepLinkOpenedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkVideoId || !deepLinkVideoRow) return;
    if (videoDeepLinkOpenedFor.current === deepLinkVideoId) return;
    videoDeepLinkOpenedFor.current = deepLinkVideoId;
    setVideoPopup(deepLinkVideoRow);
  }, [deepLinkVideoId, deepLinkVideoRow]);

  // ── Deep-link reader: on-page case ──────────────────────────────────────────
  // When ?panel=script-finalized|content-finalized&creator=<username> is
  // present AND the creator is found in the currently-loaded finalized rows,
  // open the read-only dialog. Off-page navigation (creator not loaded yet) is
  // handled by the page-level useCampaignDeepLinkPanel hook, which resolves
  // the page and navigates; once the row is in `creators`, this effect fires.
  //
  // Legacy compatibility: links written before Phase B used ?panel=script-review
  // / ?panel=content-review on the Finalized tab (disambiguated by ?tab=confirmed).
  // We continue to honor those values here so existing shared links keep working,
  // while new writes use the dedicated finalized values.
  //
  // Open-once guard: deepLinkOpenedFor tracks "creator|panel" to avoid
  // reopening after the user manually closes the dialog (mirrors the
  // relandedFor ref pattern in useCampaignDeepLinkPanel).
  const { panel: dlPanel, creator: dlCreator, thread: dlThread } = useSearch({
    strict: false,
  }) as Partial<CampaignSearch>;
  const deepLinkOpenedFor = useRef<string | null>(null);
  const finalisedMode: 'script' | 'content' | null =
    dlPanel === 'script-finalized' || dlPanel === 'script-review' ? 'script'
    : dlPanel === 'content-finalized' || dlPanel === 'content-review' ? 'content'
    : null;
  const isFinalisedDeepLink = !!finalisedMode && !!dlCreator;
  useEffect(() => {
    if (!isFinalisedDeepLink || !dlCreator || !dlPanel || !finalisedMode) return;
    const key = `${dlCreator}|${dlPanel}`;
    if (deepLinkOpenedFor.current === key) return;

    const row = (allFinalizedQuery.data ?? []).find((r) => r.platform?.username === dlCreator);
    if (!row) return; // creator not loaded yet — page-level resolver navigates first

    deepLinkOpenedFor.current = key;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot deep-link resolver: opens the panel only once per creator|panel key (guarded by deepLinkOpenedFor ref) as the finalized roster loads async; not a simple prop→state sync.
    setReadOnlyDialog({ creator: row, mode: finalisedMode });
  }, [isFinalisedDeepLink, dlCreator, dlPanel, finalisedMode, allFinalizedQuery.data]);

  // Stable entry ID derived from whichever dialog is open. On the
  // unified Finalized shape `c.id` IS the roster_entry ObjectID — no
  // need to probe roster_entry_id / _id fallbacks.
  const dialogEntryId = readOnlyDialog?.creator?.id ?? '';
  const dialogMode = readOnlyDialog?.mode ?? null;

  // Per-mode entry IDs gate the detail / thread hooks. Each hook is
  // disabled (entryId empty string) when the dialog is in the other
  // mode — so opening Script doesn't fire a Content fetch and vice
  // versa. Same staleTime defaults the hooks bake in (30s detail / 0
  // thread) — no manual useQuery wrappers any more.
  const scriptEntryId = dialogMode === 'script' ? dialogEntryId : '';
  const contentEntryId = dialogMode === 'content' ? dialogEntryId : '';

  const scriptDetailQuery = useScriptDetail(scriptEntryId);
  const scriptDetail: ScriptDetail | null = scriptDetailQuery.data ?? null;
  const scriptDetailLoading = scriptDetailQuery.isLoading;

  const contentDetailQuery = useContentDetail(contentEntryId);
  const contentDetail = contentDetailQuery.data ?? null;
  const contentDetailLoading = contentDetailQuery.isLoading;

  // Thread messages — same cache key as ScriptsTab / ContentTab so a
  // user who already opened the entry in the live review tab gets
  // instant data on Finalized.
  const scriptThreadQuery = useRosterThread(scriptEntryId, 'script');
  const contentThreadQuery = useRosterThread(contentEntryId, 'content');

  // Projection is shared (components/common/mapThreadMessages) — Finalized is a
  // read-only history of the SAME thread the Scripts / Content tabs write to,
  // so it must carry every field they do (this surface previously dropped
  // sender_id/edited and the "edited" marker never rendered here).

  // Memoized: these feed the panel's openAnchors memo — fresh arrays per
  // render would re-clear + repaint the script highlights on every
  // ConfirmedTab re-render.
  const threadEntries: ReviewThreadEntry[] = useMemo(
    () =>
      dialogMode === 'script'
        ? mapThreadMessages(scriptThreadQuery.data)
        : mapThreadMessages(contentThreadQuery.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogMode, scriptThreadQuery.data, contentThreadQuery.data],
  );

  // Optional render override exposed by the embedding app (brand +
  // admin both wire it via CampaignDetailsPage / CampaignDetailPage).
  // When present, it returns the cleaner 3-column ScriptReviewPanel
  // (script body + AI analysis + chat thread); when absent we fall
  // back to the default AiDraftReviewDialog. Mirrors the same usage
  // pattern in ScriptsTab so the Finalized read-only view matches the
  // live Script-tab review surface byte-for-byte.



  // Tab-aggregated KPI — BE-computed actuals (no FE sum, no
  // page-snapshot math). Numbers stay stable when the user switches
  // sub-tabs or paginates — same Stripe / Linear / GitHub headline
  // pattern as the Creators-tab KPI strip.
  const kpi = kpiQuery.data;

  // Admin share deep-link for the confirmed/finalized tab.

  // Multi-Product Campaigns: the per-creator assignment mutation. productCatalog
  // + showProducts are computed up top (above the roster-tabs hook) so the
  // Unassigned pill can gate on them.
  const {
    mutate: assignProducts,
    isPending: assignPending,
    variables: assignVariables,
  } = useAssignProducts(campaignId ?? '');
  const canResolvePreviousProducts = useCanResolvePreviousProducts();

  // ── Column definitions ────────────────────────────────────────────────────────
  // Server-persisted column visibility (Columns dropdown) — holds per user
  // across devices/browsers; see useColumnPreferences.
  const columnState = useRosterColumnPreferences(campaignId, 'confirmed');

  const columns = useMemo((): ColumnDef<RosterEntryFinalized>[] => [
    {
      id: 'creator',
      alwaysVisible: true,
      sortable: canSort,
      header: 'Creator',
      // Uncapped grow with a low floor: Creator yields width first so the
      // admin table (incl. the pinned Action column) fits a ~1130px content
      // area; long names ellipsize with the cell's own tooltip.
      minWidth: 224,
      grow: true,
      sticky: 'left',
      cell: (c) => {
        const post = mainPost(c);
        const postStatus = post?.status;
        const sendBriefVisible =
          !!permissions?.canSendBrief &&
          (postStatus === 'script_pending' ||
            postStatus === 'script_review' ||
            postStatus === 'content_pending' ||
            postStatus === 'content_review' ||
            postStatus === 'live_pending' ||
            postStatus === 'live');
        const sendingThisRow = confirmingBriefId === c.id;
        const held = isRowHeld(post);
        const heldBriefReasonId = `held-brief-reason-${c.id}`;

        // In flow, never over the name button's hit area.
        const briefButton = (
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            onClick={held ? undefined : () => guardSend(c.id, () => handleConfirmBrief(c))}
            disabled={!held && confirmingBriefId !== null}
            aria-label="Send brief"
            aria-disabled={held || undefined}
            aria-describedby={held ? heldBriefReasonId : undefined}
            className="shrink-0 border border-(--border-brand-default) bg-card text-(--brand-700) opacity-0 pointer-events-none transition-opacity duration-150 hover:bg-(--brand-50) focus:pointer-events-auto focus:opacity-100 group-hover/creator:pointer-events-auto group-hover/creator:opacity-100 disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
          >
            {sendingThisRow ? (
              <span className="size-3 animate-spin rounded-full border-2 border-(--brand-200) border-t-(--brand-600)" />
            ) : (
              <PaperPlaneTilt />
            )}
          </Button>
        );

        return (
          /* Identity opens the analytics drawer (matches the Creators tab); the
             ChartBar chip on the right edge is the always-visible affordance.
             Send Brief sits inline to its left, revealed on cell hover/focus.
             All three are sibling buttons in a plain div — never nested — so the
             handle stays plain text (its profile link lives in the drawer). */
          <div className="group/creator -mx-2 -my-1.5 flex w-full items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-muted/60">
            <button
              type="button"
              onClick={() =>
                isPublicView ? handlePublicViewInsights(c) : openInsights(c as unknown as RosterRowRef)
              }
              data-public-allowed=""
              className="flex min-w-0 flex-1 items-center rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title="View analysis"
            >
              <PlatformCreatorCell
                creator={c as unknown as Record<string, unknown>}
                campaignPlatform={campaignPlatform}
                linkHandle={false}
              />
            </button>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {sendBriefVisible ? (
                <>
                  <SimpleTooltip content={held ? HELD_ACTION_REASON : 'Send brief'}>{briefButton}</SimpleTooltip>
                  {held ? (
                    <span id={heldBriefReasonId} className="sr-only">
                      {HELD_ACTION_REASON}
                    </span>
                  ) : null}
                </>
              ) : null}
              <CreatorAnalysisButton
                onOpen={() =>
                  isPublicView ? handlePublicViewInsights(c) : openInsights(c as unknown as RosterRowRef)
                }
                creatorName={c.creator?.name ?? c.platform?.username}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      alwaysVisible: true,
      header: 'Status',
      // Fixed (not grow) — fits every status label this pill actually shows in
      // the Finalized tab ("Content Pending" is the widest at ~134px badge
      // width; needs cell padding on top of that). Trimmed from 160:
      // "Changes Requested" (151px) is a pre-finalization script/content
      // state that shouldn't recur once a creator has been locked in, so it
      // isn't budgeted for here — the Deliverables column below gives up the
      // rest of the space this needed to keep the full row at full table
      // width (both nav rails collapsed) without horizontal scroll.
      minWidth: 168,
      maxWidth: 168,
      cell: (c) => {
        const post = mainPost(c);
        return (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge status={post?.status ?? c.status} />
            <NoCodeChip show={hasNoCode(c.id)} />
            {showHold && post && <HoldStatusLine post={post} />}
          </div>
        );
      },
    },
    {
      id: 'deliverables',
      alwaysVisible: true,
      header: 'Deliverables',
      // Fixed — badges stack vertically (see cell below), so width only needs
      // to fit the single widest pill ("Non Collab Reel" ≈ 105px), not a sum
      // of several. 160/220 reserved far more than any real content uses.
      minWidth: 122,
      maxWidth: 136,
      // Cell renders rounded-full deliverable-type chips, not plain text.
      skeletonShape: 'pill',
      cell: (c) => {
        // Public mode: the adapted row carries NO posts/addons (see
        // ConfirmedTab.publicRosterRowAdapter.ts's file header — the public
        // Confirmed payload never includes production-phase data at all), so
        // `mainPost(c)` below is always undefined and this cell would
        // otherwise render "—" even for a row with a real selected
        // deliverable. `c.deliverables` IS genuinely present on the public
        // wire (BE emits the selected one(s) for a Confirmed-style entry —
        // see buildPublicRosterRow) and is a real field on
        // RosterEntryFinalized itself, so read it directly here instead.
        // There is no platform data available in public mode to split main
        // vs. add-on (campaignPlatform is undefined — its own query is
        // disabled), so every selected type renders as the same neutral chip
        // rather than guessing a category.
        if (isPublicView) {
          const labels = c.deliverables
            .filter((d) => d.is_selected)
            .map((d) => fmtDeliverableType(d.type));
          if (labels.length === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="flex flex-col items-start gap-1">
              {labels.map((label) => (
                <span
                  key={label}
                  className="inline-flex w-fit items-center rounded-full bg-(--brand-50) px-2 py-0.5 text-[12px] font-medium text-(--brand-700)"
                >
                  {label}
                </span>
              ))}
            </div>
          );
        }
        const post = mainPost(c);
        const usageRightsDays = post?.usage_rights_days;
        if (!post && c.addons.length === 0)
          return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-col items-start gap-1">
            {post && (
              <span className="inline-flex w-fit items-center rounded-full bg-(--brand-50) px-2 py-0.5 text-[12px] font-medium text-(--brand-700)">
                {fmtDeliverableType(post.type)}
              </span>
            )}
            {c.addons.map((a) => (
              <span
                key={a.id}
                className="inline-flex w-fit items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[12px] font-medium text-muted-foreground"
              >
                + {fmtDeliverableType(a.type)}
              </span>
            ))}
            {usageRightsDays != null && usageRightsDays > 0 && (
              <span className="inline-flex w-fit items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {usageRightsDays}d usage rights
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'total_cost',
      header: 'Total Cost',
      // Fixed — sized for the exact 2-decimal amount (moneyFitClass shrinks longer ones).
      minWidth: 132,
      maxWidth: 132,
      align: 'right',
      cell: (c) => {
        // No isPublicView skeleton here (2026-08-05 parity decision): Total
        // Cost is this ROW's own figure, not a campaign-wide aggregate — a
        // public grant already scopes the visitor to exactly this
        // page/page-size of rows. estimated_budget is real on an adapted
        // public row now — see ConfirmedTab.publicRosterRowAdapter.ts.
        const totalCost = c.estimated_budget;
        const cost = totalCost > 0 ? formatMoneyExact(totalCost) : '—';
        return (
          <span className={`font-medium text-foreground tabular-nums ${moneyFitClass(cost)}`}>
            {cost}
          </span>
        );
      },
    },
    {
      id: 'cpv',
      label: 'Est. CPV',
      sortable: canSort,
      header: (
        <span className="inline-flex items-center justify-end gap-1">
          Est. CPV
          <SimpleTooltip content={EST_CPV_TOOLTIP}>
            <span className="inline-flex cursor-default text-muted-foreground">
              <Info size={13} />
            </span>
          </SimpleTooltip>
        </span>
      ),
      // Fixed — "₹XX.XX" never needs more than this.
      minWidth: 80,
      maxWidth: 90,
      align: 'right',
      cell: (c) => {
        // No isPublicView skeleton here (2026-08-06 decision): estimated_cpv
        // is this ROW's own committed figure (main-post cost ÷ estimated
        // views, frozen at acceptance) — the same row-scoped-money reasoning
        // Total Cost above already carries. estimated_cpv is real on an
        // adapted public row now — see ConfirmedTab.publicRosterRowAdapter.ts.
        return <EstimatedCPVCell cpv={c.estimated_cpv} metrics={c.snapshot_metrics} now={Date.now()} />;
      },
    },
    {
      // id stays 'actions' so saved column preferences keep matching.
      id: 'actions',
      alwaysVisible: true,
      header: 'Production',
      // Fixed — this cell renders three fixed-width children: two w-24 (96px)
      // FinalizedRowAction buttons (Script, Content) plus a w-20 (80px) video
      // thumbnail box, joined by gap-2 (8px). 96*2 + 80 + 8*2 = 288px minimum.
      // Thumbnail trimmed from w-24/gap-3 (312px total) to close the
      // full-width horizontal-scroll gap (both nav rails collapsed) — the
      // buttons can't shrink further without clipping their "Script"/
      // "Content" labels, so the purely-decorative preview gives up the space.
      minWidth: 288,
      maxWidth: 300,
      align: 'center',
      cell: (c) => {
        const post = mainPost(c);
        const showScript = canViewScript(c, campaignPipeline);
        const showContent = canViewContent(c, campaignPipeline);
        const liveVideo = post?.live_video;
        const liveUrl = liveVideo?.url ?? '';
        const showLive = !!liveUrl;
        const thumb = liveVideoThumbnailUrl(liveVideo) ?? undefined;
        const kind = liveVideo?.content_kind;
        const held = isRowHeld(post);

        // Not-yet-added states: same button, same width, same enabled look —
        // just a Plus affordance instead of the disabled placeholder, wired to
        // the identical shared modal + API the live Scripts/Content/Live tabs
        // use (AddScriptModal / UrlInputModal, confirmScriptUpload — same
        // route as onAddScript, matches what ScriptsTab itself calls —
        // handleAdminSubmitContent / handleSetLiveUrl).
        const canAddScript =
          !held &&
          pipeline.script_enabled &&
          !showScript &&
          post?.status === 'script_pending' &&
          !!permissions?.canSubmitScriptOnBehalf &&
          typeof confirmScriptUpload === 'function';
        // Deliberately content_pending only, not content_review: once content
        // has been submitted it's under brand review, and there's no existing
        // URL on this row's shape to pre-fill a re-submit with (unlike
        // ContentTab, which carries live_url) — re-submitting from here would
        // look like a fresh add. content_review rows fall through to the
        // pre-existing disabled state (can't view, can't add).
        const canAddContent =
          !held &&
          pipeline.content_enabled &&
          !showContent &&
          post?.status === 'content_pending' &&
          !!permissions?.canSubmitContentOnBehalf &&
          typeof handleAdminSubmitContent === 'function';
        const canAddLive =
          !held && !showLive && post?.status === 'live_pending' && typeof handleSetLiveUrl === 'function';

        return (
          <div className="flex items-center justify-center gap-2">
            {(pipeline.script_enabled || showScript) && (
            <FinalizedRowAction
              icon={canAddScript ? <Plus size={13} /> : <Article size={13} />}
              label="Script"
              enabled={showScript || canAddScript}
              disabledReason={held ? HELD_ACTION_REASON : undefined}
              onClick={
                showScript
                  ? () => openFinalized(c, 'script')
                  : canAddScript
                    ? () => setAddScriptRow({ id: c.id, name: rowDisplayName(c) })
                    : undefined
              }
            />
            )}
            {(pipeline.content_enabled || showContent) && (
            <FinalizedRowAction
              icon={canAddContent ? <Plus size={13} /> : <VideoCamera size={13} />}
              label="Content"
              enabled={showContent || canAddContent}
              disabledReason={held ? HELD_ACTION_REASON : undefined}
              onClick={
                showContent
                  ? () => openFinalized(c, 'content')
                  : canAddContent
                    ? () =>
                        setAddContentRow({
                          id: c.id,
                          existingUrl: '',
                          expectedContentKind: deliverableContentKind(post?.type),
                        })
                    : undefined
              }
            />
            )}
            <DisabledReason reason={held && !showLive ? HELD_ACTION_REASON : null}>
              <div
                role={canAddLive ? 'button' : undefined}
                aria-label={canAddLive ? 'Add live URL' : undefined}
                data-testid="live-thumb"
                className={`relative h-[54px] w-20 shrink-0 overflow-hidden rounded-lg bg-muted ${showLive || canAddLive ? 'cursor-pointer' : ''}`}
                onClick={
                  showLive
                    ? () => openVideo(c, c.id)
                    : canAddLive
                      ? () =>
                          setAddLiveUrlRow({
                            id: c.id,
                            expectedContentKind: deliverableContentKind(post?.type),
                          })
                      : undefined
                }
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    className="size-full object-cover"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center text-muted-foreground">
                    {canAddLive ? <Plus size={18} /> : <Play size={18} />}
                  </div>
                )}
                {kind && (
                  <span className="absolute bottom-1 left-1 rounded-sm bg-black/60 px-1 py-0.5 text-[9px] font-bold leading-none text-white">
                    {contentKindLabel(kind)}
                  </span>
                )}
                {showLive && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/20 opacity-0 transition-opacity hover:opacity-100">
                    <Play size={20} className="fill-white text-white" />
                  </div>
                )}
              </div>
            </DisabledReason>
          </div>
        );
      },
    },
    ...(showProducts
      ? [
          {
            id: 'products',
            header: 'Products',
            // Fixed, not just a floor — ProductAssignCell clips overflowing
            // chips to one line and surfaces the full list via tooltip
            // instead of growing the column with the assignment count.
            minWidth: 240,
            maxWidth: 240,
            cell: (c: RosterEntryFinalized) => {
              const held = isRowHeld(mainPost(c));
              const locked = isProductsLocked(c);
              if (canResolvePreviousProducts && !held && shouldOfferPreviousProducts(c)) {
                return <PreviousProductsPrompt row={c} />;
              }
              return (
                <ProductAssignCell
                  assigned={(c.assigned_products ?? []).map((p) => ({
                    product_id: p.product_id,
                    name: p.name,
                  }))}
                  campaignProducts={productCatalog}
                  canEdit={!!permissions?.canAssignProducts && !held && !locked}
                  disabledReason={held ? HELD_ACTION_REASON : locked ? PRODUCTS_LOCKED_LABEL : undefined}
                  onAssign={(ids) => assignProducts({ entryId: c.id, productIds: ids })}
                  // Only disable the row whose assignment is actually in flight —
                  // the mutation instance is shared across the column, so gate on the
                  // in-flight entryId instead of the global isPending (which would
                  // freeze every other row's +/× controls during one assignment).
                  pending={assignPending && assignVariables?.entryId === c.id}
                />
              );
            },
          } as ColumnDef<RosterEntryFinalized>,
        ]
      : []),
  ], [campaignPlatform, confirmingBriefId, permissions?.canSendBrief, permissions?.canAssignProducts, permissions?.canSubmitScriptOnBehalf, permissions?.canSubmitContentOnBehalf, confirmScriptUpload, handleAdminSubmitContent, handleSetLiveUrl, showProducts, productCatalog, assignProducts, assignPending, canResolvePreviousProducts, assignVariables, isPublicView, canSort, showHold, isRowHeld, handleConfirmBrief, guardSend, hasNoCode, openInsights, handlePublicViewInsights, openFinalized, openVideo, campaignPipeline, pipeline.script_enabled, pipeline.content_enabled]);

  const rowMenuColumn = useConfirmedRowMenuColumn({
    enabled: showRowMenu,
    canManageHold,
    canRollback,
    pipeline: campaignPipeline,
    isRowHeld,
    isHoldWired: !!holdPost,
    isReleaseWired: !!releasePost,
    isRollbackWired: !!rollbackApproval,
    isAcceptanceRollbackWired: !!rollbackAcceptance,
    openHold,
    openRelease,
    openRollback,
  });

  // The row menu column stays out of the Columns menu and always renders last.
  const columnView = useMemo(() => {
    const view = columnVisibilityView(columns, columnState.hiddenIds);
    return { ...view, visibleColumns: withTrailingColumn(view.visibleColumns, rowMenuColumn) };
  }, [columns, columnState.hiddenIds, rowMenuColumn]);

  const goToCreatorsPending = () =>
    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({ ...p, tab: 'creators', pill: 'pending' })) as never,
    });

  const emptyState =
    allCount === 0 ? (
      <EmptyState
        icon={UserCheck}
        title="No creators finalized yet"
        body="Approve creators first. Once you greenlight them, our team confirms rates and availability, and they land here ready to script."
        action={{ label: 'Review pending creators', onClick: goToCreatorsPending }}
      />
    ) : phaseTab === 'script' ? (
      <EmptyState
        icon={FileText}
        title="No creators in the Script phase"
        body="Finalized creators working on script show here."
      />
    ) : phaseTab === 'content' ? (
      <EmptyState
        icon={VideoCamera}
        title="No creators in the content phase"
        body="Finalized creators working on content show here."
      />
    ) : phaseTab === 'live' ? (
      <EmptyState
        icon={ChartLineUp}
        title="No creators in the live phase"
        body="They appear here once live."
      />
    ) : phaseTab === 'hold' ? (
      <EmptyState icon={UserCheck} title={holdCopy.emptyState.title} body={holdCopy.emptyState.body} />
    ) : (
      <EmptyState
        icon={PaperPlaneTilt}
        title="Nothing in this phase yet"
        body="Creators will appear as they progress."
      />
    );

  // Public-mode pagination display (grant snapshot, not the disabled hook's
  // own) — mirrors CreatorsTab.tsx's identical block verbatim, see its doc
  // comment for the full rationale. Same `total_pages > 1` condition drives
  // both `hideSummary` here and the granted pill's badge (displayPills
  // above) — the two must never disagree about whether the total is known.
  const publicPageSize = grant?.pageSize ?? pageSize;
  const publicGrantedRowCount = rosterPage?.rows.length ?? 0;
  const displayPage = isPublicView ? (rosterPage?.page ?? grant?.page ?? 1) : page;
  const displayTotalPages = isPublicView
    ? (rosterPage?.total_pages ?? grant?.totalPages ?? 1)
    : pageCount;
  const hideSummary = isPublicView && displayTotalPages > 1;
  const displayTotal = isPublicView
    ? displayTotalPages > 1
      ? Math.max(publicGrantedRowCount, 1)
      : publicGrantedRowCount
    : total;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-5">
      {/* Figma content header above the KPI strip. */}
      <RosterScreenHeader title="Finalized creators" subtitle="Creators locked into this campaign." />
      <RosterKpiCard
        reach={kpi?.estimated_views ?? 0}
        budget={kpi?.locked_budget ?? 0}
        creatorCount={kpi?.creator_count ?? 0}
        creatorCountLabel="Finalized"
        reachLabel={EST_VIEWS_LABEL}
        budgetLabel="Locked Budget"
        // A DISABLED react-query hook reports isLoading: false (v5:
        // isLoading = isPending && isFetching, and a never-fetched disabled
        // query has isFetching: false) — so kpiQuery.isLoading alone would
        // render 0/₹0 as if that were the real answer.
        loading={isPublicView || kpiQuery.isLoading}
      />

      <RosterTableShell<FinalizedSubTab>
        fill
        activeTab={phaseTab}
        // No-op in public mode — belt-and-braces alongside the public
        // shell's click-interceptor (see PUBLIC_NOOP's doc comment).
        // Public mode prompts sign-in (with the click captured for resume)
        // instead of switching pills directly — see handlePublicPillClick.
        onTabChange={isPublicView ? handlePublicPillClick : setPhaseTab}
        // Pills show counts from /roster/counts, which do NOT refetch on
        // pagination — so their skeleton tracks counts availability, not the
        // per-page row fetch (which would flash the pills on every page turn).
        // Public mode never shows the whole-row skeleton — displayPills
        // already carries a per-pill 'loading' sentinel for every pill
        // except the granted one.
        tabsLoading={isPublicView ? false : !rosterCounts}
        // Search is a real interactive control with no meaning against a
        // single already-fetched page in public mode — hidden entirely
        // rather than wired to a no-op, mirroring CreatorsTab's `controls`
        // treatment.
        controls={
          isPublicView ? undefined : (
            <RosterSearchSort
              query={searchState.query}
              onQueryChange={searchState.setQuery}
              columnsMenu={
                <ColumnsMenu
                  items={columnView.menuItems}
                  hiddenCount={columnView.hiddenCount}
                  onToggle={columnState.toggle}
                  onReset={columnState.reset}
                />
              }
            />
          )
        }
        tabs={displayPills}
        page={displayPage}
        totalPages={displayTotalPages}
        total={displayTotal}
        // Never a fabricated campaign-wide total — see displayTotal above.
        hideSummary={hideSummary}
        onPage={isPublicView ? PUBLIC_NOOP : setPage}
        pageSize={isPublicView ? publicPageSize : pageSize}
        pageSizeOptions={isPublicView ? undefined : PAGE_SIZE_OPTIONS}
        onPageSizeChange={isPublicView ? undefined : setPageSize}
      >
        {/* DataTable — border-0/rounded-none so the Card shell provides the
            outer border + rounding; the table scrolls horizontally inside
            the Card's overflow-hidden container. */}
        <DataTable<RosterEntryFinalized>
          columns={columnView.visibleColumns}
          data={displayedRows}
          getRowId={(c) => c.id}
          sort={rosterSort.sortProp(effectivePhaseTab, { disabled: !canSort, onChange: () => setPage(1) })}
          // isLoadingEntries (from the hook's disabled, forever-unsettled
          // query) would stay true forever in public mode — but the real
          // granted rows are ALREADY available synchronously from the public
          // payload, so the table must render them immediately rather than
          // sitting in a permanent row-skeleton state.
          loading={isPublicView ? false : isLoadingEntries}
          emptyState={emptyState}
          fillHeight
          className="border-0 rounded-none bg-transparent"
          rowClassName={showRowMenu ? ROW_MENU_OPEN_ROW_CLASS : undefined}
        />
      </RosterTableShell>

      {/*
        Read-only Script / Content side-panel for finalized rows.
        Reuses AiDraftReviewDialog with `canReview=false` +
        `canAddComment=false` so every interactive control is gone:
        no Approve, no Request-changes, no chat composer. The panel
        renders the locked artifact body + version + AI analysis +
        chat history (frozen audit trail) — exactly what the user
        asked for ("script sidebar with latest approved script").
        Closing the dialog clears the dialog state which also drops
        scriptDetail / contentDetail / threadEntries via the effect
        above, so the next row opens fresh.
      */}
      {readOnlyDialog
        ? (() => {
            // Read-only side panels for the Finalized tab.
            // Script button → ScriptReviewPanel (canReview=false, canAddComment=false).
            // Content button → ContentReviewPanel (same read-only flags).
            // Bodies + AI come from the per-entry detail hooks
            // (useScriptDetail / useContentDetail); version + status meta
            // come from posts[0].script / posts[0].content.
            const creator = readOnlyDialog.creator;
            const dialogPost = mainPost(creator);
            const platform = creator.platform;
            const creatorName =
              creator.creator?.name ||
              platform.name ||
              platform.username ||
              'Creator';
            const creatorHandle = platform.username || undefined;
            const creatorAvatarUrl = platform.profile_picture_url || undefined;
            // Production phase from the main post (frozen roster status
            // is useless here); fall back to a neutral label.
            const statusLabel = dialogPost?.status
              ? dialogPost.status.replace(/_/g, ' ')
              : 'finalized';

            if (readOnlyDialog.mode === 'script') {
              // Derive stable AI envelope — same pattern as ScriptsTab.
              // While scriptDetail is loading, show pending spinner so the
              // Script tab stays readable. Once loaded, pass the real analysis.
              const aiEnvelope: ScriptReviewAiEnvelope | undefined =
                scriptDetailLoading && !scriptDetail
                  ? { status: 'pending' }
                  : (scriptDetail?.ai_analysis as ScriptReviewAiEnvelope | undefined);
              // Version: prefer the live detail body's version, else the
              // slim post.script meta carried on the row.
              const scriptVersion = scriptDetail?.version ?? dialogPost?.script?.version;
              return (
                <ScriptReviewPanel
                  key={creator.id}
                  open
                  onClose={closeFinalized}
                  title="Script review"
                  creatorName={creatorName}
                  creatorHandle={creatorHandle}
                  creatorAvatarUrl={creatorAvatarUrl}
                  shareCreatorUsername={platform.username || undefined}
                  shareTab="confirmed"
                  sharePanel="script-finalized"
                  statusLabel={statusLabel}
                  uploadedLabel={fmtDateTime(scriptDetail?.uploaded_at) || undefined}
                  versionLabel={scriptVersion ? `v${scriptVersion}` : undefined}
                  doc={scriptDetail?.doc}
                  scriptTextLoading={scriptDetailLoading}
                  scriptPdfUrl={scriptDetail?.pdf_url}
                  aiAnalysis={aiEnvelope}
                  scriptVersionId={scriptDetail?.version_id}
                  // Approved GEO pack, read-only. The geo envelope comes from
                  // the same per-entry script detail (scriptDetail.geo_analysis);
                  // geoReadOnly renders the approved pack + persisted selection
                  // with no generate/regenerate/select-edit. onGenerate/
                  // onRegenerate/onSaveSelection are intentionally omitted so
                  // nothing can mutate the locked pack.
                  geoAnalysis={scriptDetail?.geo_analysis}
                  showGeo={(campaignPlatform ?? '').toLowerCase().includes('youtube')}
                  geoReadOnly
                  thread={threadEntries}
                  // Brand can approve directly from Confirmed when a locked-in
                  // creator's script is still sitting in script_review — same
                  // gate as the Scripts tab (canApproveScript is brand-only;
                  // admin never approves scripts, per BE rule). Once the post
                  // moves past script_review this collapses back to read-only.
                  canReview={!!permissions?.canApproveScript && dialogPost?.status === 'script_review'}
                  reviewBusy={scriptActionBusy}
                  isApproved={justApprovedIds.has(creator.id)}
                  onApprove={() => void handleApproveScript(creator.id)}
                  canAddComment={false}
                  // Finalized creators can still have a post sitting in
                  // script_review (locked in, but script review not done
                  // yet) — Add to Comment only makes sense there; once the
                  // post has moved on (e.g. content_pending) this view is
                  // purely a read-only history and shouldn't invite edits.
                  canAddLineComment={dialogPost?.status === 'script_review'}
                  threadDefaultOpen={dlThread === 'open'}
                />
              );
            }


            // Content variant — the live video URL (once published) is on
            // posts[0].live_video; the review body comes from the content
            // detail hook. No legacy content_meta.url / live_url fallbacks.
            const videoPrimary = contentDetail?.url ?? dialogPost?.live_video?.url ?? '';

            return (
              <ContentReviewPanel
                key={creator.id}
                open
                onClose={closeFinalized}
                title="AI Content Review"
                creatorName={creatorName}
                creatorAvatarUrl={creatorAvatarUrl}
                creatorHandle={creatorHandle}
                // Enable the read-only GEO ('geo') tab on the finalized panel.
                // `creator.id` IS the roster_entry ObjectID (see dialogEntryId).
                // GEO finalize is now prop-fed (ContentDetail.geo_finalize) and
                // arrives async off the BE webhook, not fetched client-side —
                // entryId no longer gates visibility, showGeo does. geoReadOnly
                // hides Re-finalize/Rephrase/Select — this tab is a read-only
                // audit view for every viewer (no onRegenerateGeoFinalize/
                // onRephraseGeoElement/onSaveGeoSelection are wired here).
                entryId={creator.id}
                geoAnalysis={contentDetail?.geo_finalize}
                showGeo={(campaignPlatform ?? '').toLowerCase().includes('youtube')}
                geoReadOnly
                shareCreatorUsername={platform.username || undefined}
                shareTab="confirmed"
                sharePanel="content-finalized"
                statusLabel={statusLabel}
                updatedLabel={fmtDateTime(contentDetail?.uploaded_at) || ''}
                videoUrl={videoPrimary}
                leftExtras={<ContentRowDetail c={creator as unknown as Record<string, unknown>} layout="embed" hideReviewActions />}
                canReview={false}
                reviewBusy={false}
                onApprove={() => {}}
                thread={threadEntries}
                viewerActor={permissions?.canSubmitContentOnBehalf ? 'admin' : 'brand'}
                canAddComment={false}
                addCommentBusy={false}
                defaultThreadOpen={dlThread === 'open'}
                aiAnalysis={
                  contentDetailLoading && !contentDetail
                    ? { status: 'pending' }
                    : (contentDetail?.ai_analysis ?? { status: 'pending' })
                }
              />
            );
          })()
        : null}

      {videoPopup && (() => {
        // Live video lives on the main post under the posts/addons cutover.
        const popupVideo = mainPost(videoPopup)?.live_video;
        const url = popupVideo?.url ?? '';
        const thumb = liveVideoThumbnailUrl(popupVideo) ?? undefined;
        const { username, profilePicture } = resolveCreatorPlatformProfile(
          videoPopup as unknown as Record<string, unknown>,
          campaignPlatform,
        );
        return (
          <VideoPlayerPopup
            videoUrl={url}
            thumbnailUrl={thumb}
            creatorName={rowDisplayName(videoPopup)}
            creatorHandle={username || undefined}
            creatorAvatarUrl={profilePicture || undefined}
            platform={campaignPlatform ?? 'youtube'}
            contentKind={popupVideo?.content_kind}
            views={Number(popupVideo?.statistics?.view_count ?? 0)}
            likes={Math.max(0, Number(popupVideo?.statistics?.like_count ?? 0))}
            comments={Math.max(0, Number(popupVideo?.statistics?.comment_count ?? 0))}
            onClose={closeVideo}
          />
        );
      })()}

      {/* Add Script / Add Content / Add Live URL — the exact same shared
          modals + APIs as ScriptsTab / ContentTab / LiveContentTab, reached
          one phase early from Confirmed's not-yet-added row actions. */}
      {addScriptRow && (
        <AddScriptModal
          key={addScriptRow.id}
          open
          onClose={() => setAddScriptRow(null)}
          creatorName={addScriptRow.name}
          creatorId={addScriptRow.id}
          rosterEntryId={addScriptRow.id}
          onScriptSubmit={handleAddScript}
          isLoading={addScriptLoading}
        />
      )}

      <UrlInputModal
        open={!!addContentRow}
        onClose={() => setAddContentRow(null)}
        onSave={async (url) => {
          if (!addContentRow || typeof handleAdminSubmitContent !== 'function') return;
          await handleAdminSubmitContent(addContentRow.id, { url });
          setAddContentRow(null);
          void queryClient.invalidateQueries({
            queryKey: campaignKeys.rosterTab(campaignId ?? '', 'finalized'),
          });
        }}
        title="Add content URL"
        description={uploadCopy(campaignPlatform).description}
        initialUrl={addContentRow?.existingUrl ?? ''}
        placeholder={uploadCopy(campaignPlatform).placeholder}
        saveLabel="Save URL"
        requirePlatform={uploadCopy(campaignPlatform).requirePlatform}
        expectedContentKind={addContentRow?.expectedContentKind}
      />

      <UrlInputModal
        open={!!addLiveUrlRow}
        onClose={() => setAddLiveUrlRow(null)}
        onSave={async (url) => {
          if (!addLiveUrlRow || !handleSetLiveUrl) return;
          await handleSetLiveUrl(addLiveUrlRow.id, url);
          setAddLiveUrlRow(null);
          void queryClient.invalidateQueries({
            queryKey: campaignKeys.rosterTab(campaignId ?? '', 'finalized'),
          });
        }}
        title="Add live URL"
        description={liveUrlCopy(campaignPlatform).description}
        placeholder={liveUrlCopy(campaignPlatform).placeholder}
        saveLabel="Save URL"
        requirePlatform={liveUrlCopy(campaignPlatform).requirePlatform}
        expectedContentKind={addLiveUrlRow?.expectedContentKind}
      />

      {holdDialogs}
      {rollbackDialogs}
      {noCodeDialog}
    </div>
  );
}
